import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, Calendar, Clock, ArrowUpRight, Building, MoreHorizontal } from 'lucide-react';
import { api } from '../services/api';

const fmtBRL = (v) => (new Intl.NumberFormat('pt-BR',{style:'currency',currency:'BRL'})).format(Number(v||0));

const createEmptyCardStats = () => ({
  pagar: { quantidade: 0, total: 0 },
  receber: { quantidade: 0, total: 0 }
});

const normalizeCardStats = (data) => ({
  pagar: {
    quantidade: Number(data?.pagar?.quantidade || 0),
    total: Number(data?.pagar?.total || 0)
  },
  receber: {
    quantidade: Number(data?.receber?.quantidade || 0),
    total: Number(data?.receber?.total || 0)
  }
});

const DEFAULT_CARD_SETTINGS = Object.freeze({
  atrasadas: {
    title: 'Atrasadas',
    theme: 'red',
    filters: { status: ['vencida'] }
  },
  venceHoje: {
    title: 'Vence Hoje',
    theme: 'yellow',
    filters: { periodIncludeToday: true }
  },
  proximos7: {
    title: 'Próximos 7 Dias',
    theme: 'indigo',
    filters: { periodFutureEnabled: true, periodFutureDays: '7', periodIncludeToday: false }
  }
});

const createDefaultCardSettings = () => JSON.parse(JSON.stringify(DEFAULT_CARD_SETTINGS));

const COLOR_THEMES = {
  indigo: {
    label: 'Indigo',
    borderClass: 'border-indigo-300 dark:border-indigo-700',
    iconClass: 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400',
    accentClass: 'text-indigo-500',
    pagarCountClass: 'text-indigo-500',
    receberCountClass: 'text-indigo-400'
  },
  red: {
    label: 'Vermelho',
    borderClass: 'border-red-400 dark:border-red-600',
    iconClass: 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400',
    accentClass: 'text-red-500',
    pagarCountClass: 'text-red-500',
    receberCountClass: 'text-emerald-500'
  },
  yellow: {
    label: 'Amarelo',
    borderClass: 'border-yellow-400 dark:border-yellow-600',
    iconClass: 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400',
    accentClass: 'text-yellow-500',
    pagarCountClass: 'text-yellow-500',
    receberCountClass: 'text-emerald-500'
  },
  green: {
    label: 'Verde',
    borderClass: 'border-emerald-400 dark:border-emerald-700',
    iconClass: 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400',
    accentClass: 'text-emerald-500',
    pagarCountClass: 'text-emerald-500',
    receberCountClass: 'text-emerald-400'
  },
  blue: {
    label: 'Azul',
    borderClass: 'border-blue-400 dark:border-blue-600',
    iconClass: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400',
    accentClass: 'text-blue-500',
    pagarCountClass: 'text-blue-500',
    receberCountClass: 'text-sky-500'
  }
};

const STATUS_BADGE_COLORS = {
  green: 'bg-green-500/20 text-green-400 border-green-500/30',
  red: 'bg-red-500/20 text-red-400 border-red-500/30',
  yellow: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  orange: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  blue: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
};

const PROXIMOS_FUTURE_OPTIONS = [
  { value: '7', label: 'Próximos 7 dias' },
  { value: '15', label: 'Próximos 15 dias' },
  { value: '30', label: 'Próximos 30 dias' },
  { value: 'all', label: 'Todos os futuros' },
  { value: 'custom', label: 'Intervalo personalizado' },
];

const parseDateOnly = (value) => {
  if (!value) return null;

  if (typeof value === 'string') {
    const normalized = value.trim();
    const isoMatch = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoMatch) {
      const [, year, month, day] = isoMatch;
      const parsed = new Date(Number(year), Number(month) - 1, Number(day));
      if (!Number.isNaN(parsed.getTime())) {
        parsed.setHours(0, 0, 0, 0);
        return parsed;
      }
    }
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  date.setHours(0, 0, 0, 0);
  return date;
};

const getDaysUntilDue = (lancamento) => {
  if (!lancamento) return null;
  const diffRaw = Number(lancamento.dias_para_vencimento);
  if (!Number.isNaN(diffRaw)) {
    return diffRaw;
  }

  const dueDate = parseDateOnly(lancamento.data_vencimento);
  if (!dueDate) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((dueDate - today) / (1000 * 60 * 60 * 24));
};

const computePrazoStatus = (lancamento) => {
  const fallback = { status: '', diff: null };
  if (!lancamento) return fallback;

  const statusRaw = typeof lancamento.status_vencimento === 'string'
    ? lancamento.status_vencimento.toLowerCase()
    : '';

  const diff = getDaysUntilDue(lancamento);

  if (statusRaw === 'vencida' || statusRaw === 'hoje' || statusRaw === 'futuro') {
    return { status: statusRaw, diff };
  }

  if (diff === null) {
    return fallback;
  }

  if (diff < 0) return { status: 'vencida', diff };
  if (diff === 0) return { status: 'hoje', diff };
  return { status: 'futuro', diff };
};

const computeBadgesForLancamento = (lancamento) => {
  if (!lancamento?.data_vencimento) {
    return [];
  }

  const badges = [];
  const statusCalcRaw = (lancamento.status_calculado || lancamento.status_pagamento || lancamento.status || '').toLowerCase();
  const statusPagamento = (() => {
    if (statusCalcRaw === 'pago' || statusCalcRaw === 'paga') return 'concluida';
    if (statusCalcRaw === 'parcialmente_pago') return 'parcial';
    return statusCalcRaw;
  })();

  const dueDate = parseDateOnly(lancamento.data_vencimento);
  if (!dueDate) {
    return badges;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.round((dueDate - today) / (1000 * 60 * 60 * 24));

  if (statusPagamento === 'concluida') {
    badges.push({ tipo: 'pagamento', label: 'Concluída', color: 'green' });
    return badges;
  }

  if (statusPagamento === 'parcial' || statusPagamento === 'parcialmente_pago') {
    badges.push({ tipo: 'pagamento', label: 'Parcialmente pago', color: 'yellow' });
  }

  if (statusPagamento !== 'concluida') {
    if (diffDays < 0) {
      const abs = Math.abs(diffDays);
      badges.push({
        tipo: 'prazo',
        label: 'Vencida',
        color: 'red',
        prazo: abs,
        prazoLabel: abs === 1 ? 'há 1 dia' : `há ${abs} dias`
      });
    } else if (diffDays === 0) {
      badges.push({
        tipo: 'prazo',
        label: 'Vence Hoje',
        color: 'orange',
        prazo: 0,
        prazoLabel: 'Hoje'
      });
    } else {
      badges.push({
        tipo: 'prazo',
        label: 'Pendente',
        color: 'blue',
        prazo: diffDays,
        prazoLabel: diffDays === 1 ? 'em 1 dia' : `em ${diffDays} dias`
      });
    }
  }

  return badges;
};

const getPrazoDisplay = (lancamento) => {
  const dueDate = parseDateOnly(lancamento?.data_vencimento);
  if (!dueDate) {
    return { text: '', colorClass: '' };
  }

  const { status, diff } = computePrazoStatus(lancamento);
  const dateLabel = dueDate.toLocaleDateString('pt-BR');

  if (status === 'vencida') {
    const abs = diff !== null ? Math.abs(diff) : null;
    const suffix = abs !== null ? ` (há ${abs} ${abs === 1 ? 'dia' : 'dias'})` : '';
    return { text: `Venceu em ${dateLabel}${suffix}`, colorClass: 'text-red-500' };
  }

  if (status === 'hoje') {
    return { text: `Vence hoje (${dateLabel})`, colorClass: 'text-amber-500' };
  }

  if (status === 'futuro') {
    const suffix = diff !== null ? `${diff === 1 ? '1 dia' : `${diff} dias`}` : '';
    const extra = suffix ? ` (${suffix})` : '';
    return { text: `Vence em ${dateLabel}${extra}`, colorClass: 'text-blue-500' };
  }

  return { text: `Vence em ${dateLabel}`, colorClass: 'text-blue-500' };
};

export const buildRowQueryParams = (lancamento) => {
  const params = {};
  if (!lancamento) return params;

  if (lancamento.tipo) {
    params.tipo = lancamento.tipo;
  }

  if (lancamento.id !== undefined && lancamento.id !== null) {
    params.lancamentoId = lancamento.id;
  }

  const statusSet = new Set();
  const statusCalc = (lancamento.status_calculado || lancamento.status_pagamento || lancamento.status || '').toLowerCase();

  if (statusCalc === 'concluida' || statusCalc === 'pago' || statusCalc === 'paga') {
    statusSet.add('concluida');
  } else if (statusCalc === 'parcial' || statusCalc === 'parcialmente_pago') {
    statusSet.add('parcialmente_pago');
  } else {
    statusSet.add('pendente');
  }

  const { status } = computePrazoStatus(lancamento);
  if (status === 'vencida') {
    statusSet.add('vencida');
  }

  if (statusSet.size > 0) {
    params.status = Array.from(statusSet).join(',');
  }

  if (status === 'hoje') {
    params.vencimento = 'hoje';
  } else if (status === 'futuro') {
    const dueDate = parseDateOnly(lancamento.data_vencimento);
    if (dueDate) {
      const iso = toDateInputValue(dueDate);
      params.data_inicio = iso;
      params.data_fim = iso;
    }
  }

  return params;
};

const FILTER_DEFAULTS = {
  tipo: '',
  status: [],
  data_inicio: '',
  data_fim: '',
  periodPastEnabled: false,
  periodPastDays: '',
  periodFutureEnabled: false,
  periodFutureDays: '',
  periodIncludeToday: true,
};

const STATUS_OPTIONS = [
  { key: 'pendente', label: 'Pendente', values: ['pendente'] },
  { key: 'parcial', label: 'Parcial / Parcialmente pago', values: ['parcial', 'parcialmente_pago'] },
  { key: 'concluida', label: 'Concluída', values: ['concluida'] },
  { key: 'vencida', label: 'Vencida/Atrasada', values: ['vencida'] },
  { key: 'cancelado', label: 'Cancelado', values: ['cancelado'] },
];

const STATUS_ALIAS_MAP = {
  pendentes: 'pendente',
  parcial: 'parcial',
  'parcialmente pago': 'parcialmente_pago',
  'parcialmente_pago': 'parcialmente_pago',
  concluidas: 'concluida',
  'concluídas': 'concluida',
  paga: 'concluida',
  pagas: 'concluida',
  pago: 'concluida',
  vencidas: 'vencida',
  atrasadas: 'vencida',
  atrasada: 'vencida',
  cancelada: 'cancelado',
  canceladas: 'cancelado',
};

const canonicalizeStatus = (value) => {
  if (typeof value !== 'string') return value;
  const normalized = value.trim().toLowerCase();
  if (!normalized) return '';
  return STATUS_ALIAS_MAP[normalized] || normalized;
};

const toDateInputValue = (date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const addDays = (date, amount) => {
  const result = new Date(date);
  result.setDate(result.getDate() + amount);
  return result;
};

const toBoolean = (value, fallback = false) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    if (value.toLowerCase() === 'true') return true;
    if (value.toLowerCase() === 'false') return false;
  }
  if (typeof value === 'number') {
    if (value === 1) return true;
    if (value === 0) return false;
  }
  return fallback;
};

const buildParamsFromFilters = (filters = {}) => {
  const params = {};
  if (filters.tipo) params.tipo = filters.tipo;

  if (Array.isArray(filters.status) && filters.status.length > 0) {
    const uniqueStatuses = Array.from(new Set(filters.status.filter(Boolean)));
    if (uniqueStatuses.length > 0) {
      params.status = uniqueStatuses.join(',');
    }
  } else if (typeof filters.status === 'string' && filters.status) {
    params.status = filters.status;
  }

  const includeToday = toBoolean(filters.periodIncludeToday, FILTER_DEFAULTS.periodIncludeToday);
  const pastEnabled = toBoolean(filters.periodPastEnabled, FILTER_DEFAULTS.periodPastEnabled);
  const futureEnabled = toBoolean(filters.periodFutureEnabled, FILTER_DEFAULTS.periodFutureEnabled);

  const pastDaysRaw = Number(filters.periodPastDays);
  const futureDaysRaw = Number(filters.periodFutureDays);
  const pastDays = pastEnabled && !Number.isNaN(pastDaysRaw) ? Math.max(0, pastDaysRaw) : 0;
  const futureDays = futureEnabled && !Number.isNaN(futureDaysRaw) ? Math.max(0, futureDaysRaw) : 0;

  let startOffset = null;
  let endOffset = null;

  if (pastEnabled) {
    startOffset = -pastDays;
    if (!futureEnabled && !includeToday) {
      endOffset = -1;
    }
  }

  if (futureEnabled) {
    endOffset = futureDays;
    if (!pastEnabled && !includeToday) {
      startOffset = startOffset === null ? 1 : startOffset;
    }
  }

  if (includeToday) {
    startOffset = Math.min(startOffset ?? 0, 0);
    endOffset = Math.max(endOffset ?? 0, 0);
  }

  if (startOffset === null && endOffset !== null) {
    startOffset = includeToday ? 0 : 1;
  }

  if (endOffset === null && startOffset !== null) {
    endOffset = includeToday ? 0 : -1;
  }

  if (startOffset !== null && endOffset !== null && startOffset <= endOffset) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startDate = addDays(today, startOffset);
    const endDate = addDays(today, endOffset);
    params.data_inicio = toDateInputValue(startDate);
    params.data_fim = toDateInputValue(endDate);
  }

  if (filters.data_inicio) params.data_inicio = filters.data_inicio;
  if (filters.data_fim) params.data_fim = filters.data_fim;

  return params;
};

const sanitizeFilters = (filters = {}) => {
  const sanitized = {};

  Object.entries(filters || {}).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      const cleaned = value
        .map((item) => (typeof item === 'string' ? item.trim() : item))
        .filter((item) => item !== undefined && item !== null && item !== '');
      if (cleaned.length > 0) {
        sanitized[key] = cleaned;
      }
      return;
    }

    if (typeof value === 'boolean') {
      sanitized[key] = value;
      return;
    }

    if (value !== undefined && value !== null && value !== '') {
      sanitized[key] = value;
    }
  });

  if (!toBoolean(filters.periodPastEnabled)) {
    delete sanitized.periodPastDays;
  }

  if (!toBoolean(filters.periodFutureEnabled)) {
    delete sanitized.periodFutureDays;
  }

  return sanitized;
};

const mergeCardSettingsWithDefaults = (rawSettings = {}) => {
  if (!rawSettings || typeof rawSettings !== 'object') {
    return createDefaultCardSettings();
  }

  const merged = { ...createDefaultCardSettings() };

  Object.entries(rawSettings).forEach(([key, value]) => {
    if (!value || typeof value !== 'object') {
      return;
    }

    const { filters, ...rest } = value;
    const sanitizedFilters = sanitizeFilters(filters || {});

    const cleaned = Object.entries(rest).reduce((acc, [field, fieldValue]) => {
      if (fieldValue === undefined || fieldValue === null) {
        return acc;
      }
      return { ...acc, [field]: fieldValue };
    }, {});

    merged[key] = {
      ...(merged[key] || {}),
      ...cleaned,
      filters: sanitizedFilters,
    };
  });

  return merged;
};

const normalizeStatusList = (value) => {
  if (Array.isArray(value)) {
    const cleaned = value
      .map((item) => canonicalizeStatus(item))
      .filter((item) => item !== undefined && item !== null && item !== '');
    return Array.from(new Set(cleaned));
  }

  if (typeof value === 'string') {
    const tokens = value
      .split(',')
      .map((token) => canonicalizeStatus(token))
      .filter(Boolean);
    return Array.from(new Set(tokens));
  }

  return [];
};

const normalizeFiltersForEditor = (rawFilters = {}) => {
  const normalized = { ...FILTER_DEFAULTS };

  normalized.tipo = rawFilters.tipo || '';
  const statusSet = new Set(normalizeStatusList(rawFilters.status));
  STATUS_OPTIONS.forEach((option) => {
    const hasAny = option.values.some((value) => statusSet.has(value));
    if (hasAny) {
      option.values.forEach((value) => statusSet.add(value));
    }
  });
  normalized.status = Array.from(statusSet);

  if (rawFilters.data_inicio) normalized.data_inicio = rawFilters.data_inicio;
  if (rawFilters.data_fim) normalized.data_fim = rawFilters.data_fim;

  normalized.periodIncludeToday = toBoolean(
    rawFilters.periodIncludeToday,
    FILTER_DEFAULTS.periodIncludeToday,
  );

  const pastEnabled = toBoolean(rawFilters.periodPastEnabled, FILTER_DEFAULTS.periodPastEnabled);
  const futureEnabled = toBoolean(rawFilters.periodFutureEnabled, FILTER_DEFAULTS.periodFutureEnabled);

  normalized.periodPastEnabled = pastEnabled;
  normalized.periodFutureEnabled = futureEnabled;

  if (rawFilters.periodPastDays !== undefined && rawFilters.periodPastDays !== null) {
    normalized.periodPastDays = `${rawFilters.periodPastDays}`;
  }

  if (rawFilters.periodFutureDays !== undefined && rawFilters.periodFutureDays !== null) {
    normalized.periodFutureDays = `${rawFilters.periodFutureDays}`;
  }

  const legacyRange = typeof rawFilters.range === 'string' ? rawFilters.range.toLowerCase() : '';
  if (legacyRange === 'relative') {
    const direction = rawFilters.relativeDirection === 'future' ? 'future' : 'past';
    const daysValue = rawFilters.relativeDays ?? '';
    const includeTodayLegacy = toBoolean(
      rawFilters.relativeIncludeToday,
      FILTER_DEFAULTS.periodIncludeToday,
    );

    normalized.periodIncludeToday = includeTodayLegacy;

    if (direction === 'past') {
      normalized.periodPastEnabled = true;
      normalized.periodPastDays = `${daysValue ?? ''}`;
    } else {
      normalized.periodFutureEnabled = true;
      normalized.periodFutureDays = `${daysValue ?? ''}`;
    }
  } else if (legacyRange) {
    const match = legacyRange.match(/^(\d+)d$/);
    if (match) {
      normalized.periodFutureEnabled = true;
      normalized.periodFutureDays = match[1];
      normalized.periodIncludeToday = true;
    }
  }

  if (typeof rawFilters.vencimento === 'string') {
    const vencimento = rawFilters.vencimento.toLowerCase();
    if (vencimento === 'hoje') {
      normalized.periodIncludeToday = true;
      normalized.periodPastEnabled = false;
      normalized.periodFutureEnabled = false;
    } else if (vencimento === 'amanha' || vencimento === 'amanhã') {
      normalized.periodIncludeToday = false;
      normalized.periodPastEnabled = false;
      normalized.periodFutureEnabled = true;
      normalized.periodFutureDays = '1';
    } else if (vencimento === 'ontem') {
      normalized.periodIncludeToday = false;
      normalized.periodFutureEnabled = false;
      normalized.periodPastEnabled = true;
      normalized.periodPastDays = '1';
    }
  }

  return normalized;
};

const resolveStatusSummary = (selectedStatuses = []) => {
  if (!Array.isArray(selectedStatuses) || selectedStatuses.length === 0) {
    return [];
  }

  const summary = [];
  const selectedSet = new Set(selectedStatuses);

  STATUS_OPTIONS.forEach((option) => {
    const hasAll = option.values.every((value) => selectedSet.has(value));
    if (hasAll) {
      summary.push(option.label);
      option.values.forEach((value) => selectedSet.delete(value));
    }
  });

  if (selectedSet.size > 0) {
    summary.push(...Array.from(selectedSet));
  }

  return summary;
};

export default function Home() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [proximosFilters, setProximosFilters] = useState({
    includeVencidos: true,
    includeHoje: true,
    includeFuturos: true,
    futureRange: '7',
    customStart: '',
    customEnd: '',
  });
  const [showProximosConfig, setShowProximosConfig] = useState(false);
  const [cardSettings, setCardSettings] = useState(() => createDefaultCardSettings());
  const [cardData, setCardData] = useState({
    atrasadas: createEmptyCardStats(),
    venceHoje: createEmptyCardStats(),
    proximos7: createEmptyCardStats()
  });
  const prevCardSettingsRef = useRef(null);
  const [editingCardKey, setEditingCardKey] = useState(null);
  const [tempSettings, setTempSettings] = useState(null);
  const statusToggleButtonRef = useRef(null);
  const statusPopoverRef = useRef(null);
  const [statusPopoverOpen, setStatusPopoverOpen] = useState(false);
  const selectedStatuses = normalizeStatusList(tempSettings?.filters?.status);
  const statusSummaryLabels = resolveStatusSummary(selectedStatuses);
  const statusSummaryText = statusSummaryLabels.length > 0
    ? statusSummaryLabels.join(', ')
    : 'Selecionar status';
  const periodPastEnabled = toBoolean(tempSettings?.filters?.periodPastEnabled);
  const periodFutureEnabled = toBoolean(tempSettings?.filters?.periodFutureEnabled);
  const periodIncludeToday = toBoolean(
    tempSettings?.filters?.periodIncludeToday,
    FILTER_DEFAULTS.periodIncludeToday,
  );

  const filteredProximos = useMemo(() => {
    const lista = Array.isArray(stats?.proximos) ? stats.proximos : [];
    if (!lista.length) {
      return [];
    }

    const customStartDate = proximosFilters.customStart ? parseDateOnly(proximosFilters.customStart) : null;
    const customEndDate = proximosFilters.customEnd ? parseDateOnly(proximosFilters.customEnd) : null;

    const filtered = lista.filter((item) => {
      const { status, diff } = computePrazoStatus(item);
      const dueDate = parseDateOnly(item.data_vencimento);

      if (!dueDate) {
        return false;
      }

      if (status === 'vencida' && !proximosFilters.includeVencidos) {
        return false;
      }

      if (status === 'hoje' && !proximosFilters.includeHoje) {
        return false;
      }

      if (status === 'futuro') {
        if (!proximosFilters.includeFuturos) {
          return false;
        }

        if (proximosFilters.futureRange === 'custom') {
          if (customStartDate && dueDate < customStartDate) {
            return false;
          }
          if (customEndDate && dueDate > customEndDate) {
            return false;
          }
        } else if (proximosFilters.futureRange !== 'all') {
          const limit = Number(proximosFilters.futureRange);
          if (!Number.isNaN(limit) && diff !== null && diff > limit) {
            return false;
          }
        }
      }

      return true;
    });

    const orderMap = { vencida: 0, hoje: 1, futuro: 2 };

    return filtered.sort((a, b) => {
      const aStatus = computePrazoStatus(a).status;
      const bStatus = computePrazoStatus(b).status;
      const aGroup = orderMap[aStatus] ?? 3;
      const bGroup = orderMap[bStatus] ?? 3;
      if (aGroup !== bGroup) {
        return aGroup - bGroup;
      }

      const aDate = parseDateOnly(a.data_vencimento)?.getTime() ?? 0;
      const bDate = parseDateOnly(b.data_vencimento)?.getTime() ?? 0;
      if (aDate !== bDate) {
        return aDate - bDate;
      }

      return (a.descricao || '').localeCompare(b.descricao || '', 'pt-BR');
    });
  }, [stats, proximosFilters]);

  const updateProximosFilter = (key, value) => {
    setProximosFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleFutureRangeChange = (value) => {
    setProximosFilters((prev) => ({
      ...prev,
      futureRange: value,
      ...(value !== 'custom' ? { customStart: '', customEnd: '' } : {}),
    }));
  };

  useEffect(() => {
    if (!statusPopoverOpen) return undefined;

    const handleInteraction = (event) => {
      if (
        statusPopoverRef.current?.contains(event.target) ||
        statusToggleButtonRef.current?.contains(event.target)
      ) {
        return;
      }
      setStatusPopoverOpen(false);
    };

    document.addEventListener('mousedown', handleInteraction);
    document.addEventListener('touchstart', handleInteraction);

    return () => {
      document.removeEventListener('mousedown', handleInteraction);
      document.removeEventListener('touchstart', handleInteraction);
    };
  }, [statusPopoverOpen]);

  useEffect(() => {
    let cancelled = false;

    const fetchPreferences = async () => {
      try {
        const data = await api.getDashboardPreferences();
        if (cancelled) return;

        const toMerge = {
          ...(typeof data?.defaults === 'object' ? data.defaults : {}),
          ...(typeof data?.preferences === 'object' ? data.preferences : {}),
        };

        setCardSettings(mergeCardSettingsWithDefaults(toMerge));
      } catch (error) {
        console.error('Erro ao carregar preferências do dashboard:', error);
        if (!cancelled) {
          setCardSettings(createDefaultCardSettings());
        }
      }
    };

    fetchPreferences();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => { load(); }, []);
  async function load() {
    try {
      const data = await api.getDashboardStats();
      setStats(data);
    } catch (e) {
      console.error('Erro ao carregar estatísticas:', e);
    } finally {
      setLoading(false);
    }
  }

  const goToLancamentos = (params) => {
    const sanitizedEntries = Object.entries(params || {}).filter(([, value]) => value !== undefined && value !== null && value !== '');
    const qs = new URLSearchParams(Object.fromEntries(sanitizedEntries));
    navigate(`/lancamentos?${qs.toString()}`);
  };

  const openSettings = (key) => {
    const current = cardSettings[key];
    if (!current) return;

    const normalizedFilters = normalizeFiltersForEditor(current.filters || {});

    setEditingCardKey(key);
    setStatusPopoverOpen(false);
    setTempSettings({
      ...current,
      theme: current.theme || 'indigo',
      filters: normalizedFilters,
    });
  };

  const closeSettings = () => {
    setEditingCardKey(null);
    setTempSettings(null);
    setStatusPopoverOpen(false);
  };

  const persistPreferences = async (nextSettings) => {
    try {
      await api.saveDashboardPreferences(nextSettings);
    } catch (error) {
      console.error('Erro ao salvar preferências do dashboard:', error);
    }
  };

  const handleTempChange = (field, value) => {
    setTempSettings((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFilterChange = (field, value) => {
    setTempSettings((prev) => ({
      ...prev,
      filters: {
        ...prev?.filters,
        [field]: value
      }
    }));
  };

  const togglePeriodFlag = (flagField, valueField, checked) => {
    setTempSettings((prev) => {
      const prevFilters = prev?.filters || {};
      const nextFilters = {
        ...prevFilters,
        [flagField]: checked,
      };

      if (!checked && valueField) {
        nextFilters[valueField] = '';
      }

      return {
        ...prev,
        filters: nextFilters,
      };
    });
  };

  const toggleStatusFilter = (statusValues) => {
    const values = Array.isArray(statusValues) ? statusValues : [statusValues];
    setTempSettings((prev) => {
      const prevFilters = prev?.filters || {};
      const currentList = normalizeStatusList(prevFilters.status);
      const currentSet = new Set(currentList);
      const hasAll = values.every((value) => currentSet.has(value));

      if (hasAll) {
        values.forEach((value) => currentSet.delete(value));
      } else {
        values.forEach((value) => currentSet.add(value));
      }

      return {
        ...prev,
        filters: {
          ...prevFilters,
          status: Array.from(currentSet),
        },
      };
    });
  };

  const saveSettings = () => {
    if (!editingCardKey || !tempSettings) return;
    const { filters: tempFilters, ...rest } = tempSettings;
    const cleanedFilters = sanitizeFilters(tempFilters || {});

    const nextSettings = {
      ...cardSettings,
      [editingCardKey]: {
        ...(cardSettings[editingCardKey] || {}),
        ...rest,
        filters: cleanedFilters,
      },
    };

    setCardSettings(nextSettings);
    persistPreferences(nextSettings);
    closeSettings();
  };

  useEffect(() => {
    let cancelled = false;

    const fetchCardStats = async (key, filters = {}) => {
      setCardData((prev) => ({
        ...prev,
        [key]: createEmptyCardStats(),
      }));

      const query = {
        ...buildParamsFromFilters(filters),
        card: '1'
      };

      try {
        const data = await api.getDashboardStats(query);
        if (cancelled) return;
        setCardData((prev) => ({
          ...prev,
          [key]: normalizeCardStats(data)
        }));
      } catch (error) {
        console.error('Erro ao carregar estatísticas do card', key, error);
        if (cancelled) return;
        setCardData((prev) => ({
          ...prev,
          [key]: createEmptyCardStats()
        }));
      }
    };

    const previousSettings = prevCardSettingsRef.current;
    const entries = Object.entries(cardSettings || {});
    const shouldFetchAll = !previousSettings;

    const toFetch = shouldFetchAll
      ? entries
      : entries.filter(([key, config]) => {
          const prevConfig = previousSettings[key] || {};
          const prevFilters = prevConfig.filters || {};
          const currentFilters = config?.filters || {};
          return JSON.stringify(prevFilters) !== JSON.stringify(currentFilters);
        });

    if (toFetch.length === 0) {
      prevCardSettingsRef.current = cardSettings;
      return () => {
        cancelled = true;
      };
    }

    toFetch.forEach(([key, config]) => {
      fetchCardStats(key, config?.filters || {});
    });

    prevCardSettingsRef.current = cardSettings;

    return () => {
      cancelled = true;
    };
  }, [cardSettings]);

  const handleNavigateFromCard = (key, extra = {}) => {
    const filters = cardSettings[key]?.filters || {};
    const baseParams = buildParamsFromFilters(filters);
    const params = { ...baseParams, ...extra };
    goToLancamentos(params);
  };

  const handleNavigateFromRow = (lancamento) => {
    const params = buildRowQueryParams(lancamento);
    goToLancamentos(params);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="spinner h-12 w-12 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Carregando dashboard...</p>
        </div>
      </div>
    );
  }

  // Helpers para montar o card dividido
  const CardDuplo = ({
    cardKey,
    settings,
    icon: Icon,
    data,
    onNavigate,
    onOpenSettings,
  }) => {
    const pagarQtd = data?.pagar?.quantidade || 0;
    const pagarTot = data?.pagar?.total || 0;
    const recQtd   = data?.receber?.quantidade || 0;
    const recTot   = data?.receber?.total || 0;

    const theme = COLOR_THEMES[settings?.theme] || COLOR_THEMES.indigo;
    const pagarHighlight = theme.pagarCountClass || theme.accentClass || 'text-red-400';
    const receberHighlight = theme.receberCountClass || theme.accentClass || 'text-green-400';

    const handleNavigate = (extra = {}) => {
      if (onNavigate) {
        onNavigate(extra);
      }
    };

    return (
      <div
        className={`card-gradient border-l-4 transition-all duration-200 cursor-pointer hover:shadow-lg hover:scale-[1.02] ${theme.borderClass}`}
        onClick={() => handleNavigate()}
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <p className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
              {settings?.title || 'Card'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onOpenSettings(cardKey);
              }}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition"
              title="Configurar card"
            >
              <MoreHorizontal className="w-5 h-5" />
            </button>
            <div className={`p-3 rounded-xl ${theme.iconClass}`}>
              <Icon className="w-6 h-6" />
            </div>
          </div>
        </div>

        {/* Linha dividida A Pagar / A Receber */}
        <div className="grid grid-cols-2 gap-4">
          <button
            className="text-left p-3 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            onClick={(e) => { e.stopPropagation(); handleNavigate({ tipo: 'pagar' }); }}
            title="Ver apenas a pagar"
          >
            <div className="text-xs uppercase text-gray-500">A Pagar</div>
            <div className={`text-3xl font-extrabold ${pagarHighlight}`}>{pagarQtd}</div>
            <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">{fmtBRL(pagarTot)}</div>
          </button>
          <button
            className="text-left p-3 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            onClick={(e) => { e.stopPropagation(); handleNavigate({ tipo: 'receber' }); }}
            title="Ver apenas a receber"
          >
            <div className="text-xs uppercase text-gray-500">A Receber</div>
            <div className={`text-3xl font-extrabold ${receberHighlight}`}>{recQtd}</div>
            <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">{fmtBRL(recTot)}</div>
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-8 animate-fade-in">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">Visão geral do seu sistema financeiro</p>
      </div>

      {/* Cards (apenas os 3 pedidos) */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">

        {/* Atrasadas */}
        <CardDuplo
          cardKey="atrasadas"
          settings={cardSettings.atrasadas}
          icon={AlertCircle}
          data={cardData.atrasadas}
          onNavigate={(opt = {}) => handleNavigateFromCard('atrasadas', opt)}
          onOpenSettings={openSettings}
        />

        {/* Vence Hoje */}
        <CardDuplo
          cardKey="venceHoje"
          settings={cardSettings.venceHoje}
          icon={Calendar}
          data={cardData.venceHoje}
          onNavigate={(opt = {}) => handleNavigateFromCard('venceHoje', opt)}
          onOpenSettings={openSettings}
        />

        {/* Próximos 7 Dias */}
        <CardDuplo
          cardKey="proximos7"
          settings={cardSettings.proximos7}
          icon={Clock}
          data={cardData.proximos7}
          onNavigate={(opt = {}) => handleNavigateFromCard('proximos7', opt)}
          onOpenSettings={openSettings}
        />
      </div>


      {/* Próximos Vencimentos (lista) */}
      {Array.isArray(stats?.proximos) ? (
        <div className="card">
          <div className="flex flex-col gap-4 mb-6 md:flex-row md:items-start md:justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Próximos Vencimentos</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">Acompanhe vencimentos vencidos, do dia e futuros com filtros personalizados.</p>
            </div>
            <div className="flex items-center gap-2 self-start">
              <button
                type="button"
                onClick={() => setShowProximosConfig((prev) => !prev)}
                className={`p-2 rounded-lg border transition-colors ${showProximosConfig
                  ? 'border-primary-500 text-primary-600 dark:text-primary-300 bg-primary-500/10'
                  : 'border-transparent text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800'}`}
                title="Configurar filtros"
              >
                <span className="sr-only">Configurar filtros da tabela</span>
                <MoreHorizontal className="w-5 h-5" />
              </button>
              <button
                onClick={() => navigate('/calendario')}
                className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-semibold text-sm flex items-center gap-1"
              >
                Ver calendário
                <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {showProximosConfig && (
            <div className="flex flex-col gap-4 mb-6 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-900/40 p-4">
              <div className="flex flex-wrap gap-3">
                <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={proximosFilters.includeVencidos}
                    onChange={(e) => updateProximosFilter('includeVencidos', e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  Exibir vencidos
                </label>
                <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={proximosFilters.includeHoje}
                    onChange={(e) => updateProximosFilter('includeHoje', e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  Exibir vencimentos de hoje
                </label>
                <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={proximosFilters.includeFuturos}
                    onChange={(e) => updateProximosFilter('includeFuturos', e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  Exibir próximos
                </label>
              </div>

              {proximosFilters.includeFuturos && (
                <div className="flex flex-wrap items-end gap-3">
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
                      Intervalo dos próximos
                    </label>
                    <select
                      value={proximosFilters.futureRange}
                      onChange={(e) => handleFutureRangeChange(e.target.value)}
                      className="rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      {PROXIMOS_FUTURE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>

                  {proximosFilters.futureRange === 'custom' && (
                    <div className="flex flex-wrap gap-3">
                      <div>
                        <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">De</label>
                        <input
                          type="date"
                          value={proximosFilters.customStart}
                          onChange={(e) => updateProximosFilter('customStart', e.target.value)}
                          className="rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Até</label>
                        <input
                          type="date"
                          value={proximosFilters.customEnd}
                          onChange={(e) => updateProximosFilter('customEnd', e.target.value)}
                          className="rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {filteredProximos.length > 0 ? (
            <div className="space-y-3">
              {filteredProximos.map((lancamento) => {
                const badges = computeBadgesForLancamento(lancamento);
                const paymentBadge = badges.find((badge) => badge.tipo === 'pagamento');
                const prazoBadge = badges.find((badge) => badge.tipo === 'prazo');
                const prazoInfo = getPrazoDisplay(lancamento);
                const totalPago = Number(lancamento.total_pago || 0);
                const valorTotal = Number(lancamento.valor || 0);
                const valorRestanteCalculado = Number(lancamento.valor_restante);
                const valorRestante = Number.isNaN(valorRestanteCalculado)
                  ? Math.max(0, valorTotal - totalPago)
                  : Math.max(0, valorRestanteCalculado);
                const valorPrincipal = valorRestante > 0 ? valorRestante : valorTotal;
                const tipoClass = lancamento.tipo === 'receber' ? 'text-emerald-500' : 'text-rose-500';

                return (
                  <div
                    key={lancamento.id}
                    className="flex flex-col gap-3 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors cursor-pointer md:flex-row md:items-center md:justify-between"
                    onClick={() => handleNavigateFromRow(lancamento)}
                  >
                    <div className="flex items-start gap-3">
                      <Building className="mt-1 text-gray-400" size={18} />
                      <div>
                        <p className="font-semibold text-gray-900 dark:text-white">{lancamento.descricao || 'Sem descrição'}</p>
                        {prazoInfo.text && (
                          <p className={`text-xs font-medium ${prazoInfo.colorClass}`}>{prazoInfo.text}</p>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-lg font-bold ${tipoClass}`}>
                        {fmtBRL(valorPrincipal)}
                      </p>
                      {totalPago > 0 && valorRestante > 0 && valorRestante < valorTotal && (
                        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                          Total {fmtBRL(valorTotal)} · Pago {fmtBRL(totalPago)}
                        </p>
                      )}
                      <div className="flex flex-wrap gap-2 justify-end mt-2">
                        {paymentBadge && (
                          <span className={`px-3 py-1.5 rounded-full text-xs font-medium border inline-block whitespace-nowrap ${STATUS_BADGE_COLORS[paymentBadge.color] || ''}`}>
                            {paymentBadge.label}
                          </span>
                        )}
                        {prazoBadge && (
                          <span className={`px-3 py-1.5 rounded-full text-xs font-medium border inline-block whitespace-nowrap ${STATUS_BADGE_COLORS[prazoBadge.color] || ''}`}>
                            {prazoBadge.label}
                            {prazoBadge.prazoLabel && (
                              <span className="ml-1 text-[11px] opacity-80">{prazoBadge.prazoLabel}</span>
                            )}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="border border-dashed border-gray-300 dark:border-gray-700 rounded-lg py-10 text-center text-sm text-gray-500 dark:text-gray-400">
              Nenhum lançamento encontrado para os filtros selecionados.
            </div>
          )}
        </div>
      ) : (
        <div className="card text-center py-12">
          <Calendar className="w-16 h-16 text-gray-400 dark:text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Nenhum vencimento próximo</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">Você está em dia com seus lançamentos!</p>
        </div>
      )}

      {editingCardKey && tempSettings && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-xl">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                saveSettings();
              }}
            >
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Configurações do Card</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Personalize título, filtros e cor do card.</p>
                </div>
                <button
                  type="button"
                  onClick={closeSettings}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                >
                  ✕
                </button>
              </div>

              <div className="px-6 py-5 space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Título</label>
                  <input
                    type="text"
                    value={tempSettings.title}
                    onChange={(e) => handleTempChange('title', e.target.value)}
                    className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Cor</label>
                  <select
                    value={tempSettings.theme}
                    onChange={(e) => handleTempChange('theme', e.target.value)}
                    className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {Object.entries(COLOR_THEMES).map(([key, value]) => (
                      <option key={key} value={key}>{value.label}</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tipo</label>
                    <select
                      value={tempSettings.filters?.tipo || ''}
                      onChange={(e) => handleFilterChange('tipo', e.target.value)}
                      className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="">Todos</option>
                      <option value="pagar">A pagar</option>
                      <option value="receber">A receber</option>
                    </select>
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                    <button
                      ref={statusToggleButtonRef}
                      type="button"
                      onClick={() => setStatusPopoverOpen((prev) => !prev)}
                      className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-left flex items-center justify-between gap-3 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <div className="min-w-0">
                        <p className="text-sm text-gray-900 dark:text-white truncate">{statusSummaryText}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {selectedStatuses.length > 0
                            ? `${selectedStatuses.length} selecionado${selectedStatuses.length > 1 ? 's' : ''}`
                            : 'Nenhum status selecionado'}
                        </p>
                      </div>
                      <span className="text-gray-400">▾</span>
                    </button>
                    {statusPopoverOpen && (
                      <div
                        ref={statusPopoverRef}
                        className="absolute left-0 top-full z-20 mt-2 w-full md:w-72 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 p-3 shadow-xl"
                      >
                        <div className="max-h-60 overflow-y-auto space-y-2">
                          {STATUS_OPTIONS.map((option) => {
                            const checked = option.values.every((value) => selectedStatuses.includes(value));
                            return (
                              <label
                                key={option.key}
                                className={`flex items-center justify-between gap-3 rounded-md border px-3 py-2 text-sm transition ${checked
                                  ? 'border-primary-500 bg-primary-500/10 text-primary-600 dark:text-primary-400'
                                  : 'border-transparent text-gray-700 dark:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600'
                                }`}
                              >
                                <span className="flex-1">{option.label}</span>
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => toggleStatusFilter(option.values)}
                                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                />
                              </label>
                            );
                          })}
                        </div>
                        <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">Selecione um ou mais status para filtrar o card.</p>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Período</label>
                  <div className="rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 space-y-4">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                        <input
                          type="checkbox"
                          checked={periodPastEnabled}
                          onChange={(e) => togglePeriodFlag('periodPastEnabled', 'periodPastDays', e.target.checked)}
                          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        Considerar dias anteriores
                      </label>
                      {periodPastEnabled && (
                        <div className="flex items-center gap-2 w-full sm:w-auto">
                          <input
                            type="number"
                            min="0"
                            step="1"
                            value={tempSettings.filters?.periodPastDays ?? ''}
                            onChange={(e) => handleFilterChange('periodPastDays', e.target.value)}
                            className="w-full sm:w-24 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                            placeholder="Dias"
                          />
                          <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">dias</span>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                        <input
                          type="checkbox"
                          checked={periodFutureEnabled}
                          onChange={(e) => togglePeriodFlag('periodFutureEnabled', 'periodFutureDays', e.target.checked)}
                          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        Considerar próximos dias
                      </label>
                      {periodFutureEnabled && (
                        <div className="flex items-center gap-2 w-full sm:w-auto">
                          <input
                            type="number"
                            min="0"
                            step="1"
                            value={tempSettings.filters?.periodFutureDays ?? ''}
                            onChange={(e) => handleFilterChange('periodFutureDays', e.target.value)}
                            className="w-full sm:w-24 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                            placeholder="Dias"
                          />
                          <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">dias</span>
                        </div>
                      )}
                    </div>
                    <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <input
                        type="checkbox"
                        checked={periodIncludeToday}
                        onChange={(e) => handleFilterChange('periodIncludeToday', e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      Considerar dia atual
                    </label>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Combine os dias anteriores e futuros para montar intervalos como "vencidas há 3 dias" ou "próximos 5 dias".
                    </p>
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-800 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={closeSettings}
                  className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition"
                >
                  Salvar alterações
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
